/**
 * Notification types and interfaces
 * Based on the backend NotificationPayload DTO
 */

/**
 * @typedef {Object} NotificationPayload
 * @property {number} id - Notification ID
 * @property {string} type - Notification type (e.g., "OVER_LIMIT_PROMPT")
 * @property {string} title - Notification title
 * @property {string} body - Notification body text
 * @property {string} [actionLabel] - Optional action button label
 * @property {string} [actionUrl] - Optional action URL
 * @property {string} createdAt - ISO date string when notification was created
 * @property {string} [expiresAt] - ISO date string when notification expires
 * @property {string} [metadataJson] - JSON string containing additional metadata
 */

/**
 * Notification types enum
 * Based on backend NotificationType enum
 */
export const NOTIFICATION_TYPES = {
  OVER_LIMIT_PROMPT: 'OVER_LIMIT_PROMPT',
  LIVESTREAM_STARTED: 'LIVESTREAM_STARTED',
  LIVESTREAM_ENDED: 'LIVESTREAM_ENDED',
  LIVESTREAM_CANCELLED: 'LIVESTREAM_CANCELLED',
  PAYMENT_SUCCESS: 'PAYMENT_SUCCESS',
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  SUBSCRIPTION_EXPIRED: 'SUBSCRIPTION_EXPIRED',
  SUBSCRIPTION_RENEWED: 'SUBSCRIPTION_RENEWED',
  QUOTA_WARNING: 'QUOTA_WARNING',
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
  // Add other notification types as needed
};

/**
 * Parse notification metadata
 * @param {NotificationPayload} notification
 * @returns {Object} Parsed metadata object
 */
export const parseNotificationMetadata = (notification) => {
  if (!notification.metadataJson) {
    return {};
  }
  
  try {
    return JSON.parse(notification.metadataJson);
  } catch (error) {
    return {};
  }
};

/**
 * Check if notification is expired
 * @param {NotificationPayload} notification
 * @returns {boolean} True if notification is expired
 */
export const isNotificationExpired = (notification) => {
  if (!notification.expiresAt) {
    return false;
  }
  
  const expiryDate = new Date(notification.expiresAt);
  const now = new Date();
  
  return now > expiryDate;
};

/**
 * Check if notification is actionable (has action button)
 * @param {NotificationPayload} notification
 * @returns {boolean} True if notification has action
 */
export const isNotificationActionable = (notification) => {
  return !!(notification.actionLabel || notification.actionUrl);
};
